/**
 * `<turn-bar>`: under the felt, the line that says what kind of move is due,
 * the note that says where the selected cards can go, and the way out.
 */

import { h, setAttribute } from "../dom.js";
import { emit } from "../events.js";
import type { TurnNote } from "../table-text.js";
import { BaseElement } from "./base-element.js";
import styles from "./turn-bar.css";

export interface TurnBarModel {
    readonly phase: string;
    readonly isYourTurn: boolean;
    readonly note: TurnNote;
    readonly selectedCount: number;
}

export class TurnBar extends BaseElement {
    #model: TurnBarModel | null = null;

    readonly #phase = h("p", { class: "phase" });

    readonly #note = h("p", { "class": "note", "aria-live": "polite" });

    readonly #clear = h("button", { type: "button", class: "quiet" }, "Clear selection");

    constructor() {
        super(styles);

        const leave = h("button", { type: "button", class: "quiet" }, "Leave table");

        this.#clear.addEventListener("click", () => emit(this, "clear-selection", null));
        leave.addEventListener("click", () => emit(this, "leave-table", null));

        this.root.append(
            h("div", { class: "text" }, this.#phase, this.#note),
            h("div", { class: "buttons" }, this.#clear, leave)
        );
    }

    set model(value: TurnBarModel | null) {
        this.#model = value;
        this.invalidate();
    }

    protected render(): void {
        const model = this.#model;

        if (model === null) {
            return;
        }

        this.#phase.textContent = model.phase;
        setAttribute(this.#phase, "data-active", model.isYourTurn);

        if (this.#note.textContent !== model.note.text) {
            this.#note.textContent = model.note.text;
        }

        setAttribute(this.#note, "data-actionable", model.note.actionable);
        this.#clear.disabled = model.selectedCount === 0;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "turn-bar": TurnBar;
    }
}

customElements.define("turn-bar", TurnBar);
